/**
 * MembersPage Component
 * List all members with search, filters, and server-side pagination
 */

import { useState, useMemo, useEffect } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { X, RefreshCw, SlidersHorizontal } from 'lucide-react';
import type { Member, Role } from '../types';
import { Layout } from '../components/layout/Layout';
import { MemberRow } from '../components/members/MemberRow';
import { MemberFormModal } from '../components/members/MemberFormModal';
import { RemoveMemberModal } from '../components/members/RemoveMemberModal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Button } from '../components/ui/Button';
import { BottomSheet } from '../components/ui/BottomSheet';
import { PillGroup } from '../components/ui/PillGroup';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Pagination } from '../components/ui/Pagination';
import { membersService } from '../services/members.service';
import { handleApiError } from '../lib/axios';
import { teamsService } from '../services/teams.service';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { usePaginationParams } from '../hooks/usePaginationParams';
import { csvCell } from '../lib/csv';

export const MembersPage = () => {
  const { user } = useAuth();
  const toast = useToast();

  // URL-based pagination state
  const {
    params,
    setPage,
    setLimit,
    setSearch,
    setFilter,
    clearFilters,
    correctPageIfNeeded,
  } = usePaginationParams({
    defaultLimit: 25,
    defaultSort: 'last_name:asc',
    scrollToTop: true,
  });

  // Local state for debounced search input
  const [searchInput, setSearchInput] = useState(params.search);

  // Modal states
  const [isMemberFormOpen, setIsMemberFormOpen] = useState(false);
  const [memberFormMode, setMemberFormMode] = useState<'add' | 'edit'>('add');
  const [editingMemberId, setEditingMemberId] = useState<number | undefined>();

  // Remove member modal state
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [hasLinkedUser, setHasActiveUser] = useState(false);
  const [linkedUserRole, setLinkedUserRole] = useState<Role | null>(null);

  // Mobile filter sheet
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');

  // Sync search input with URL params on mount
  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  // Debounced search - update URL after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== params.search) {
        setSearch(searchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, params.search, setSearch]);

  // Fetch paginated members
  const {
    data: membersResponse,
    isLoading,
    isFetching,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['members', params],
    queryFn: () =>
      membersService.getPaginated({
        page: params.page,
        limit: params.limit,
        search: params.search,
        sort: params.sort,
        team_id: params.team_id as number | undefined,
        gender: params.gender as 'male' | 'female' | undefined,
        status: (params.status as 'active' | 'removed' | 'all') || 'active',
      }),
    placeholderData: keepPreviousData,
  });

  // Fetch teams for filter dropdown (not paginated - need all teams)
  const { data: teamsResponse } = useQuery({
    queryKey: ['teams'],
    queryFn: teamsService.getAll,
  });

  // Query client for mutations
  const queryClient = useQueryClient();

  // Check if member has active user linked
  const checkLinkedUserMutation = useMutation({
    mutationFn: (memberId: number) => membersService.checkLinkedUser(memberId),
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: ({ memberId, force }: { memberId: number; force: boolean }) =>
      membersService.delete(memberId, force),
    onSuccess: async () => {
      toast.success('Member removed successfully');
      await queryClient.refetchQueries({ queryKey: ['members'] });
      setIsRemoveModalOpen(false);
      setMemberToRemove(null);
    },
    onError: (error: Error) => {
      toast.error(handleApiError(error) || 'Failed to remove member');
    },
  });

  // Restore member mutation
  const restoreMemberMutation = useMutation({
    mutationFn: (memberId: number) => membersService.restore(memberId),
    onSuccess: async () => {
      toast.success('Member restored successfully. Note: If this member had a user account, it remains deactivated.');
      await queryClient.refetchQueries({ queryKey: ['members'] });
    },
    onError: (error: Error) => {
      toast.error(handleApiError(error) || 'Failed to restore member');
    },
  });

  // Extract data from responses
  const members = membersResponse?.data ?? [];
  const meta = membersResponse?.meta ?? { page: 1, limit: 25, total: 0, total_pages: 1 };
  const teamsData = teamsResponse ?? [];

  // Correct page if it exceeds total pages (after data loads)
  useEffect(() => {
    if (membersResponse?.meta) {
      correctPageIfNeeded(membersResponse.meta.total_pages);
    }
  }, [membersResponse?.meta, correctPageIfNeeded]);

  // Get teams for filter dropdown
  const teams = useMemo(() => {
    return teamsData.map((team) => ({
      value: String(team.id),
      label: team.name,
    }));
  }, [teamsData]);

  // Get current team filter as array for SearchableSelect
  const teamFilterValues = useMemo(() => {
    const teamIds = params.team_id;
    if (!teamIds) return ['all'];
    // Support comma-separated team IDs
    return String(teamIds).split(',');
  }, [params.team_id]);

  // Count active filters (excludes search)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (params.gender) count++;
    if (params.team_id) count++;
    if (hasAdminAccess(user?.role) && params.status && params.status !== 'active') count++;
    return count;
  }, [params.gender, params.team_id, params.status, user?.role]);

  // Modal handlers
  const handleAddMember = () => {
    setMemberFormMode('add');
    setEditingMemberId(undefined);
    setIsMemberFormOpen(true);
  };

  const handleEditMember = (memberId: number) => {
    setMemberFormMode('edit');
    setEditingMemberId(memberId);
    setIsMemberFormOpen(true);
  };

  const handleRemoveMember = async (memberId: number) => {
    const member = members.find((m) => m.id === memberId);
    if (!member) return;

    setMemberToRemove(member);

    try {
      const result = await checkLinkedUserMutation.mutateAsync(memberId);
      setHasActiveUser(result.has_linked_user);
      setLinkedUserRole(result.user_role ?? null);
      setIsRemoveModalOpen(true);
    } catch {
      toast.error('Failed to check user status');
    }
  };

  const handleConfirmRemove = () => {
    if (!memberToRemove) return;
    removeMemberMutation.mutate({
      memberId: memberToRemove.id,
      force: hasLinkedUser,
    });
  };

  const handleRestoreMember = (memberId: number) => {
    restoreMemberMutation.mutate(memberId);
  };

  // Filter handlers
  const handleTeamFilterChange = (values: string[]) => {
    if (values.includes('all') || values.length === 0) {
      setFilter('team_id', null);
    } else {
      // Store multiple team IDs as comma-separated string
      setFilter('team_id', values.join(','));
    }
  };

  const handleGenderFilterChange = (value: string) => {
    setFilter('gender', value === 'all' ? null : value);
  };

  const handleStatusFilterChange = (value: string) => {
    setFilter('status', value === 'active' ? null : value);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchInput('');
    clearFilters();
  };

  // Export members to CSV (exports current filtered/searched results)
  const handleExport = async () => {
    // Fetch all matching members for export (export=true bypasses pagination limit)
    try {
      const exportData = await membersService.getPaginated({
        page: 1,
        limit: 100,
        search: params.search,
        sort: params.sort,
        team_id: params.team_id as number | undefined,
        gender: params.gender as 'male' | 'female' | undefined,
        status: (params.status as 'active' | 'removed' | 'all') || 'active',
        export: true, // Bypasses pagination limit
      });

      const membersToExport = exportData.data;

      // Helper function to escape CSV field

      // Helper function to format date as YYYY-MM-DD
      const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '';
        try {
          return new Date(dateStr).toISOString().split('T')[0];
        } catch {
          return '';
        }
      };

      // Helper function to calculate experience
      const calculateExperience = (joinDate: string): string => {
        const start = new Date(joinDate);
        const now = new Date();

        // Return N/A for future join dates
        if (start > now) return 'N/A';

        const years = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
        const totalYears = Math.floor(years);
        const months = Math.floor((years - totalYears) * 12);

        if (totalYears === 0 && months === 0) return 'Less than 1 month';
        if (totalYears === 0) return `${months} month${months !== 1 ? 's' : ''}`;
        if (months === 0) return `${totalYears} year${totalYears !== 1 ? 's' : ''}`;
        return `${totalYears} year${totalYears !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
      };

      // Helper function to get teams led by a member (multi-leads: check the leads array)
      const getTeamsLed = (memberId: number): string => {
        const ledTeams = teamsData.filter((t) =>
          (t.leads ?? []).some((l) => l.member_id === memberId)
        );
        return ledTeams.map((t) => t.name).join(' | ');
      };

      // Generate CSV
      const headers = [
        'First Name',
        'Last Name',
        'Email',
        'Team',
        'Leads Teams',
        'Job Title',
        'Date of Birth',
        'Date Joined',
        'Experience',
        'Gender',
      ];
      const csvRows = [headers.join(',')];

      for (const member of membersToExport) {
        const row = [
          csvCell(member.first_name),
          csvCell(member.last_name),
          csvCell(member.email),
          csvCell(member.team_name || ''),
          csvCell(getTeamsLed(member.id)),
          csvCell(member.position || ''),
          formatDate(member.date_of_birth),
          formatDate(member.date_joined),
          csvCell(calculateExperience(member.date_joined)),
          csvCell(member.gender || ''),
        ];
        csvRows.push(row.join(','));
      }

      const csvContent = csvRows.join('\n');

      // Create blob with UTF-8 BOM for international character support
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `members_export_${timestamp}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(
        `Successfully exported ${membersToExport.length} member${membersToExport.length !== 1 ? 's' : ''}`
      );
    } catch {
      toast.error('Failed to export members');
    }
  };

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="text-center py-12 bg-surface rounded-lg shadow">
          <p className="text-red-600 dark:text-red-400 mb-4">Failed to load members. Please try again.</p>
          <Button onClick={() => refetch()} variant="secondary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-heading flex items-center">
              <svg
                className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Members
            </h1>
          </div>
          {hasAdminAccess(user?.role) && (
            <div className="flex flex-wrap gap-4">
              <Button variant="secondary" onClick={handleExport} className="whitespace-nowrap">
                <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Export
              </Button>
              <Button onClick={handleAddMember} className="whitespace-nowrap">
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Member
              </Button>
            </div>
          )}
        </div>

        {/* Filters - Desktop (>= lg) */}
        <div className="hidden lg:block bg-surface rounded-lg shadow p-4">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="search-input" className="block text-xs font-bold text-body mb-1">
                Search Members
              </label>
              <Input
                id="search-input"
                type="search"
                placeholder="Type name or job title"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="min-w-[140px]">
              <label htmlFor="gender-filter" className="block text-xs font-bold text-body mb-1">
                Filter by Gender
              </label>
              <Select
                id="gender-filter"
                value={(params.gender as string) || 'all'}
                onChange={(e) => handleGenderFilterChange(e.target.value)}
                options={[
                  { value: 'all', label: 'All Genders' },
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                ]}
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-bold text-body mb-1">Filter by Team</label>
              <SearchableSelect
                id="team-filter"
                multiple
                values={teamFilterValues}
                onMultiChange={handleTeamFilterChange}
                options={[{ value: 'all', label: 'All Teams' }, ...teams]}
                placeholder="Search teams..."
              />
            </div>
            {hasAdminAccess(user?.role) && (
              <div className="min-w-[170px]">
                <label htmlFor="status-filter" className="block text-xs font-bold text-body mb-1">
                  Filter by Membership
                </label>
                <Select
                  id="status-filter"
                  value={(params.status as string) || 'active'}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  options={[
                    { value: 'active', label: 'Current Members' },
                    { value: 'removed', label: 'Former Members' },
                    { value: 'all', label: 'All Members' },
                  ]}
                />
              </div>
            )}
            <div className="ml-auto">
              <Button variant="tertiary" onClick={handleClearFilters} className="flex items-center gap-2">
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Filters - Mobile (< lg) */}
        <div className="lg:hidden bg-surface rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0">
              <label htmlFor="search-input-mobile" className="block text-xs font-bold text-body mb-1">
                Search Members
              </label>
              <Input
                id="search-input-mobile"
                type="search"
                placeholder="Type name or job title"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="text-base px-4 py-3 sm:text-sm sm:px-3 sm:py-2"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="tertiary"
                onClick={() => setFiltersSheetOpen(true)}
                className="flex items-center gap-2"
              >
                <SlidersHorizontal className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
              </Button>
              <Button variant="tertiary" onClick={handleClearFilters} className="flex items-center gap-2">
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile filters bottom sheet */}
        <BottomSheet
          isOpen={filtersSheetOpen}
          onClose={() => setFiltersSheetOpen(false)}
          title="Filters"
          footer={
            <div className="flex gap-6">
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
              <Button
                variant="primary"
                onClick={() => setFiltersSheetOpen(false)}
                className="flex-1"
              >
                Apply Filters
              </Button>
            </div>
          }
        >
          <div className="space-y-6">
            <PillGroup
              label="Filter by Gender"
              value={(params.gender as string) || 'all'}
              options={[
                { value: 'all', label: 'All Genders' },
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
              ]}
              onChange={handleGenderFilterChange}
            />

            <div>
              <label className="block text-xs font-bold text-body mb-2">Filter by Team</label>
              <Input
                type="search"
                placeholder="Search teams..."
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                className="text-base px-4 py-3"
              />
              <div className="mt-2 max-h-56 overflow-y-auto border border-[var(--border-default)] rounded-md divide-y divide-[var(--border-default)]">
                <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-surface-hover">
                  <input
                    type="checkbox"
                    checked={teamFilterValues.includes('all')}
                    onChange={() => handleTeamFilterChange(['all'])}
                    className="h-4 w-4 accent-blue-600"
                  />
                  <span className="text-sm text-body font-medium">All Teams</span>
                </label>
                {teams
                  .filter((t) => t.label.toLowerCase().includes(teamSearch.toLowerCase()))
                  .map((team) => {
                    const checked = teamFilterValues.includes(team.value);
                    return (
                      <label key={team.value} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-surface-hover">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const current = teamFilterValues.filter((v) => v !== 'all');
                            const next = checked
                              ? current.filter((v) => v !== team.value)
                              : [...current, team.value];
                            handleTeamFilterChange(next);
                          }}
                          className="h-4 w-4 accent-blue-600"
                        />
                        <span className="text-sm text-body">{team.label}</span>
                      </label>
                    );
                  })}
              </div>
            </div>

            {hasAdminAccess(user?.role) && (
              <PillGroup
                label="Filter by Membership"
                value={(params.status as string) || 'active'}
                options={[
                  {
                    value: 'active',
                    label: (
                      <>
                        <span className="sm:hidden">Current</span>
                        <span className="hidden sm:inline">Current Members</span>
                      </>
                    ),
                  },
                  {
                    value: 'removed',
                    label: (
                      <>
                        <span className="sm:hidden">Former</span>
                        <span className="hidden sm:inline">Former Members</span>
                      </>
                    ),
                  },
                  { value: 'all', label: 'All Members' },
                ]}
                onChange={handleStatusFilterChange}
              />
            )}
          </div>
        </BottomSheet>

        {/* Page Info */}
        {meta.total > 0 && (
          <div className="text-sm text-meta">
            Page {meta.page} of {meta.total_pages}{'\u00A0\u00A0'}•{'\u00A0\u00A0'}{meta.total} members total
          </div>
        )}

        {/* Members List */}
        <div className="relative">
          {/* Loading overlay (keeps previous data visible) */}
          {isFetching && !isLoading && (
            <div className="absolute top-2 right-2 z-10">
              <LoadingSpinner size="sm" />
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" text="Loading members..." />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-lg shadow">
              <svg
                className="mx-auto h-12 w-12 text-meta"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {params.search || params.team_id || params.gender ? (
                <div className="mt-2">
                  <p className="text-sm text-meta">
                    No results for "{params.search}". Try adjusting your filters.
                  </p>
                  <Button variant="tertiary" onClick={handleClearFilters} className="mt-4">
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className="mt-2">
                  <p className="text-sm text-meta">No members yet. Add your first member.</p>
                  {hasAdminAccess(user?.role) && (
                    <Button onClick={handleAddMember} className="mt-4">
                      Add Member
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-surface rounded-lg shadow divide-y divide-[var(--border-default)]">
              {members.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  onEdit={handleEditMember}
                  onRemove={handleRemoveMember}
                  onRestore={handleRestoreMember}
                  dataUpdatedAt={dataUpdatedAt}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {meta.total > 0 && (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.total_pages}
            entityName="members"
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={isFetching}
          />
        )}
      </div>

      {/* Modals */}
      <MemberFormModal
        isOpen={isMemberFormOpen}
        onClose={() => setIsMemberFormOpen(false)}
        mode={memberFormMode}
        memberId={editingMemberId}
      />
      <RemoveMemberModal
        isOpen={isRemoveModalOpen}
        onClose={() => {
          setIsRemoveModalOpen(false);
          setMemberToRemove(null);
        }}
        onConfirm={handleConfirmRemove}
        memberName={memberToRemove ? `${memberToRemove.first_name} ${memberToRemove.last_name}` : ''}
        hasLinkedUser={hasLinkedUser}
        linkedUserRole={linkedUserRole}
        isRemoving={removeMemberMutation.isPending}
      />
    </Layout>
  );
};
